import { useEffect, useRef, useState } from 'react';

export type StreamTimerState = {
  elapsed: number; // ms since first frame
  remaining: number | null; // ms remaining from last duration change, null when unlimited
};

const UNLIMITED_MS = 3_600_000;

export function useStreamTimer(streamVisibilityDuration: number): {
  state: StreamTimerState;
  startTimer: () => void;
  stopTimer: () => void;
} {
  const [elapsed, setElapsed] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const durationChangedAtRef = useRef<number | null>(null);
  const durationRef = useRef(streamVisibilityDuration);

  const stopTimer = (): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startedAtRef.current = null;
    durationChangedAtRef.current = null;
    setElapsed(0);
    setRemaining(null);
  };

  const startTimer = (): void => {
    stopTimer();
    const now = Date.now();
    startedAtRef.current = now;
    durationChangedAtRef.current = now;
    durationRef.current = streamVisibilityDuration;
    setElapsed(0);
    const isUnlimited = streamVisibilityDuration >= UNLIMITED_MS;
    setRemaining(isUnlimited ? null : streamVisibilityDuration);

    intervalRef.current = setInterval(() => {
      const tick = Date.now();
      const elapsedMs = tick - (startedAtRef.current ?? tick);
      const sinceLastChange = tick - (durationChangedAtRef.current ?? tick);
      const dur = durationRef.current;
      setElapsed(elapsedMs);
      setRemaining(dur >= UNLIMITED_MS ? null : Math.max(0, dur - sinceLastChange));
    }, 1000);
  };

  // When duration changes mid-stream: reset the remaining countdown from new value
  useEffect(() => {
    durationRef.current = streamVisibilityDuration;
    if (intervalRef.current && durationChangedAtRef.current !== null) {
      durationChangedAtRef.current = Date.now();
      const isUnlimited = streamVisibilityDuration >= UNLIMITED_MS;
      setRemaining(isUnlimited ? null : streamVisibilityDuration);
    }
  }, [streamVisibilityDuration]);

  useEffect(() => () => stopTimer(), []);

  return {
    state: { elapsed, remaining },
    startTimer,
    stopTimer,
  };
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}
